import { useEffect, useRef } from 'react';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';

export function usePaymentStuckDetection() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const transactionState = useAppSelector(state => state.transaction);
  const userDetailsState = useAppSelector(state => state.userDetails);

  const stuckTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const startTimeRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // If VITE_PAYMENT_STUCK_TIMEOUT is not configured, disable the hook entirely
    const timeoutMs = Number(import.meta.env.VITE_PAYMENT_STUCK_TIMEOUT);
    if (!timeoutMs || Number.isNaN(timeoutMs)) {
      return;
    }
    // Only monitor if we're on a payment form with Stripe/Braintree, as they use reCaptcha. Also checks userDetailsState.email to ensure we're past the user-details step.
    if (!paymentSelectState.provider || !['STRIPE', 'BRAINTREE'].includes(paymentSelectState.provider) || !userDetailsState.email) {
      return;
    }

    // Start monitoring when we're on payment form but don't have a transaction ID yet
    // This indicates the /create API call hasn't succeeded yet, so the user should be staring at the loading animation
    if (!transactionState.transactionId && !startTimeRef.current) {
      startTimeRef.current = Date.now();

      stuckTimerRef.current = setTimeout(() => {
        console.warn('Timeout triggered');
        logger.error('User potentially stuck on payment form loading screen', {
          provider: paymentSelectState.provider,
          stuckDuration: Date.now() - startTimeRef.current!,
          userEmail: userDetailsState.email,
          amount: paymentSelectState.amount,
          showRecaptchaV2: transactionState.showReCaptchaV2,
          issue: 'payment-form-stuck',
        });
      }, timeoutMs);
    }

    // Clear timeout if transaction ID is obtained, transaction completes, or error occurs
    if (transactionState.transactionId || transactionState.complete || transactionState.errorMessage) {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
        stuckTimerRef.current = undefined;
      }
      startTimeRef.current = undefined;
    }

    // A cleanup function to cancel the timeout when the component unmounts
    // I need to disable the linter here because it expects a return value, but we don't need one. It still gets upset if you try to return undefined.
    // eslint-disable-next-line consistent-return
    return () => {
      if (stuckTimerRef.current) {
        clearTimeout(stuckTimerRef.current);
      }
    };
  }, [
    paymentSelectState.provider,
    paymentSelectState.amount,
    userDetailsState.email,
    transactionState.transactionId,
    transactionState.complete,
    transactionState.errorMessage,
    transactionState.showReCaptchaV2,
  ]);
}
